import moment from 'moment-timezone';
import type { MomentInput } from 'moment-timezone';
import type { TFunction } from 'i18next';
import type { Row } from 'react-table';
import type { PlannedRoute, Run } from '@bringg/types';
import i18next from 'i18next';

import type { AlertGroup } from 'bringg-web/types/common.consts';
import type { BaseRecord } from 'bringg-web/components/bringg-react-table';
import { EMPTY_ROUTE_ID, EXCEPTIONS_COLUMN_IDS } from '../../../consts';
import type { GroupByOption } from '../table-customization/group-by-dropdown/group-by-dropdown';

const getRouteLabel = (routeId: number, run: Run, plannedRoute: PlannedRoute): string => {
	if (plannedRoute?.title) {
		return plannedRoute.title;
	}

	let label: string | number;

	if (run?.external_id) {
		label = run.external_id;
	} else {
		label = routeId;
	}

	return label === EMPTY_ROUTE_ID
		? String(i18next.t('EXCEPTIONS_LIST.NO_ROUTE'))
		: `${i18next.t('EXCEPTIONS_LIST.ROUTE')} ${label}`;
};

const getIsSameDay = (firstDate: MomentInput, secondDate: MomentInput, timezone?: string) => {
	const firstDateWithTimezone = timezone ? moment(firstDate).tz(timezone) : moment(firstDate);
	const secondDateWithTimezone = timezone ? moment(secondDate).tz(timezone) : moment(secondDate);

	return moment(firstDateWithTimezone).isSame(secondDateWithTimezone, 'day');
};

const getGroupByOptionsByTab = (tab: AlertGroup, t: TFunction): GroupByOption[] => {
	const sharedOptions: GroupByOption[] = [
		{ title: t('EXCEPTIONS_LIST.NO_GROUPING'), value: [], translationKey: 'EXCEPTIONS_LIST.NO_GROUPING' },
		{
			title: t('EXCEPTIONS_LIST.ALERT'),
			value: [EXCEPTIONS_COLUMN_IDS.ALERT_TYPE],
			translationKey: 'EXCEPTIONS_LIST.ALERT'
		},
		{
			title: t('EXCEPTIONS_LIST.TEAM_AND_ALERT'),
			value: [EXCEPTIONS_COLUMN_IDS.TEAM_ID, EXCEPTIONS_COLUMN_IDS.ALERT_TYPE],
			translationKey: 'EXCEPTIONS_LIST.TEAM_AND_ALERT'
		}
	];
	const taskRelatedOptions: GroupByOption[] = [
		{
			title: t('EXCEPTIONS_LIST.TEAM_AND_ROUTE'),
			value: [EXCEPTIONS_COLUMN_IDS.TEAM_ID, EXCEPTIONS_COLUMN_IDS.ROUTE],
			translationKey: 'EXCEPTIONS_LIST.TEAM_AND_ROUTE'
		},
		{
			title: t('EXCEPTIONS_LIST.ORDER'),
			value: [EXCEPTIONS_COLUMN_IDS.EXTERNAL_ID],
			translationKey: 'EXCEPTIONS_LIST.ORDER'
		}
	];

	switch (tab) {
		case 'taskAlerts':
			return sharedOptions.concat(taskRelatedOptions);
		case 'driverAlerts':
		case 'optimizationAlerts':
		default:
			return sharedOptions;
	}
};

interface BaseSelectableRow extends BaseRecord {
	owner_id: number;
}
const getRowSelectableState = <T extends BaseSelectableRow>(row: Row<T>, currentUserId: number): boolean => {
	const isSelectable = rowItem => !rowItem.original.owner_id || rowItem.original.owner_id === currentUserId;

	if (row.isGrouped) {
		const selectableRows = row.subRows.filter(subRow => {
			return subRow.isGrouped ? getRowSelectableState(subRow, currentUserId) : isSelectable(subRow);
		});

		return Boolean(selectableRows.length);
	}
	return isSelectable(row);
};

export { getRouteLabel, getIsSameDay, getGroupByOptionsByTab, getRowSelectableState };
